import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { BasicResourceConfigurationFormProps } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadImage, isValidImageFile, isValidImageSize } from '../../../services/imageUploadService';

export const ImageConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentUrl = config?.url || config?.src || config?.image_url || resource.url || resource.src || resource.image_url || '';
  const currentAlt = config?.alt || config?.alt_text || resource.alt || resource.alt_text || '';
  const currentCaption = config?.caption || config?.description || resource.caption || resource.description || '';
  const currentAlignment = config?.alignment || config?.align || resource.alignment || resource.align || 'center';
  const currentFit = config?.fit || resource.fit || 'contain';
  const currentWidth = config?.width || config?.max_width || resource.width || resource.max_width || '';
  const currentHeight = config?.height || config?.max_height || resource.height || resource.max_height || '';

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if user is authenticated
    if (!user) {
      console.warn('[ImageConfig] Upload attempted but user is not authenticated. User:', user);
      setUploadError(t('imageConfig.notAuthenticated'));
      return;
    }
    
    console.log('[ImageConfig] User authenticated. User ID:', user.id, 'Email:', user.email, 'File:', file.name);

    // Validate file type
    if (!isValidImageFile(file)) {
      setUploadError(t('imageConfig.invalidFileType'));
      return;
    }

    // Validate file size (max 5MB)
    if (!isValidImageSize(file, 5)) {
      setUploadError(t('imageConfig.fileTooLarge'));
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const downloadURL = await uploadImage(file);
      onUpdate({ url: downloadURL, src: downloadURL, image_url: downloadURL });
      setUploadError(null);
    } catch (error: any) {
      // Translate common error messages
      let errorMessage = error.message || t('imageConfig.uploadError');
      
      if (error.message?.includes('signed in') || error.message?.includes('authenticated')) {
        errorMessage = t('imageConfig.notAuthenticated');
      } else if (error.message?.includes('CORS')) {
        errorMessage = t('imageConfig.corsError');
      } else if (error.message?.includes('Permission denied') || error.message?.includes('403') || error.code === 'storage/unauthorized') {
        // Show detailed permission error with instructions
        errorMessage = error.message || t('imageConfig.permissionError');
        // Add link to documentation if available
        if (error.message?.includes('FIREBASE_STORAGE_SETUP.md')) {
          errorMessage += '\n\n' + t('imageConfig.checkDocumentation');
        }
      }
      
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlChange = (url: string) => {
    onUpdate({ url, src: url, image_url: url });
  };

  const handleRemoveImage = () => {
    onUpdate({ url: '', src: '', image_url: '' });
    setUploadError(null);
  };


  return (
    <div className="space-y-4">
      {/* Image Preview */}
      {currentUrl && (
        <div className="relative border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-start justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">{t('imageConfig.preview')}</h4>
            <button
              type="button"
              onClick={handleRemoveImage}
              className="text-red-600 hover:text-red-800 p-1"
              title={t('imageConfig.removeImage')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex justify-center">
            <img
              src={currentUrl}
              alt={currentAlt || 'Preview'}
              className="max-h-48 max-w-full rounded-lg shadow-sm object-contain"
              onError={() => setUploadError(t('imageConfig.invalidImageUrl'))}
            />
          </div>
          {currentUrl && (
            <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
              <ExternalLink className="w-3 h-3" />
              <span className="truncate">{currentUrl}</span>
            </div>
          )}
        </div>
      )}

      {/* Authentication Warning */}
      {!user && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">{t('imageConfig.authenticationRequired')}</p>
            <p className="text-xs mt-1">{t('imageConfig.authenticationHint')}</p>
          </div>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{uploadError}</div>
        </div>
      )}

      {/* Upload Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('imageConfig.uploadImage')}
        </label>
        <div className="flex items-center gap-2">
            <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading || !user}
            className="hidden"
            id="image-upload-input"
          />
          <label
            htmlFor="image-upload-input"
            className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg transition-colors ${
              uploading || !user
                ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                : 'border-blue-300 bg-blue-50 hover:bg-blue-100 cursor-pointer'
            }`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-sm text-blue-600">{t('imageConfig.uploading')}</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-600">{t('imageConfig.selectFile')}</span>
              </>
            )}
          </label>
        </div>
        <p className="text-xs text-gray-500">{t('imageConfig.uploadHint')}</p>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-2 my-4">
        <div className="flex-1 border-t border-gray-300"></div>
        <span className="text-xs text-gray-500">{t('imageConfig.or')}</span>
        <div className="flex-1 border-t border-gray-300"></div>
      </div>

      {/* URL Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('imageConfig.imageUrl')}
        </label>
        <input
          type="url"
          value={currentUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="input-field"
        />
        <p className="text-xs text-gray-500 mt-1">{t('imageConfig.urlHint')}</p>
      </div>

      {/* Alt Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('imageConfig.altText')}
        </label>
        <input
          type="text"
          value={currentAlt}
          onChange={(e) => onUpdate({ alt: e.target.value, alt_text: e.target.value })}
          placeholder={t('imageConfig.altTextPlaceholder')}
          className="input-field"
        />
        <p className="text-xs text-gray-500 mt-1">{t('imageConfig.altTextHint')}</p>
      </div>

      {/* Caption */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('imageConfig.caption')}
        </label>
        <input
          type="text"
          value={currentCaption}
          onChange={(e) => onUpdate({ caption: e.target.value, description: e.target.value })}
          placeholder={t('imageConfig.captionPlaceholder')}
          className="input-field"
        />
      </div>

      {/* Alignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('imageConfig.alignment')}
        </label>
        <select
          value={currentAlignment}
          onChange={(e) => onUpdate({ alignment: e.target.value, align: e.target.value })}
          className="input-field"
        >
          <option value="left">{t('imageConfig.left')}</option>
          <option value="center">{t('imageConfig.center')}</option>
          <option value="right">{t('imageConfig.right')}</option>
        </select>
      </div>

      {/* Fit Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('imageConfig.fitMode')}
        </label>
        <select
          value={currentFit}
          onChange={(e) => onUpdate({ fit: e.target.value })}
          className="input-field"
        >
          <option value="contain">{t('imageConfig.contain')}</option>
          <option value="cover">{t('imageConfig.cover')}</option>
          <option value="fill">{t('imageConfig.fill')}</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">{t('imageConfig.fitModeHint')}</p>
      </div>

      {/* Width and Height */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('imageConfig.width')}
          </label>
          <input
            type="text"
            value={currentWidth}
            onChange={(e) => onUpdate({ width: e.target.value, max_width: e.target.value })}
            placeholder="auto, 100%, 500px"
            className="input-field"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('imageConfig.height')}
          </label>
          <input
            type="text"
            value={currentHeight}
            onChange={(e) => onUpdate({ height: e.target.value, max_height: e.target.value })}
            placeholder="auto, 100%, 500px"
            className="input-field"
          />
        </div>
      </div>
    </div>
  );
};

